import React, { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

const TableWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoIconWrapper = styled.div`
  cursor: pointer;
  color: #666;
  transition: color 0.3s;
  &:hover {
    color: #000;
  }
  svg {
    width: 18px;
    height: 18px;
  }
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
  margin: 10px 0 6px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
  background: #fff;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #ccc;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 12px;
  background-color: #fff;
  font-size: 0.7rem;

  th,
  td {
    border: 1px solid #ddd;
    text-align: center;
    padding: 12px 8px;
    line-height: 2.1;
  }

  th {
    background: #9848c7; /* zakupy kolor */
    color: #fff;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  tr:hover {
    background-color: #eef6ff;
  }

  .highlight-top {
    background-color: rgba(0, 128, 0, 0.08);
    font-weight: bold;
  }
  .highlight-neutral {
    background-color: #f7f7f7;
  }
`;

const monthNames = [
  "Styczeń",
  "Luty",
  "Marzec",
  "Kwiecień",
  "Maj",
  "Czerwiec",
  "Lipiec",
  "Sierpień",
  "Wrzesień",
  "Październik",
  "Listopad",
  "Grudzień",
];

const Top10PurchaseTransactions = ({ zakupyData = [] }) => {
  const [selectedYear, setSelectedYear] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [rows, setRows] = useState([]);

  const availableYears = useMemo(() => {
    const set = new Set();
    zakupyData.forEach((r) => {
      const y = parseInt(r.Rok, 10);
      if (!isNaN(y)) set.add(y);
    });
    return [...set].sort((a, b) => b - a);
  }, [zakupyData]);

  useEffect(() => {
    if (!selectedYear && availableYears.length) {
      setSelectedYear(String(availableYears[0]));
    }
  }, [availableYears, selectedYear]);

  // wartość transakcji zakupowej = K_42 + K_43
  const calculateTransactionValue = (row) => {
    const k42 = parseFloat(row.K_42) || 0;
    const k43 = parseFloat(row.K_43) || 0;
    return k42 + k43;
  };

  const getDocumentNo = (row) => row.DowodZakupu || "—";

  const fmtDate = (val) => {
    if (!val) return "Brak daty";
    const d = new Date(val);
    if (isNaN(d)) return String(val);
    return d.toLocaleDateString("pl-PL");
  };

  useEffect(() => {
    if (!zakupyData.length || !selectedYear) {
      setRows([]);
      return;
    }

    const filtered = zakupyData.filter((row) => {
      const y = parseInt(row.Rok, 10);
      const m = parseInt(row.Miesiac, 10);
      const yearMatch = String(y) === String(selectedYear);
      const monthMatch =
        selectedMonth === "all" || m === parseInt(selectedMonth, 10);
      return yearMatch && monthMatch;
    });

    const sorted = filtered
      .map((row) => ({
        supplierName: row.NazwaDostawcy || "Nieznany dostawca",
        documentNo: getDocumentNo(row),
        issueDate: row.DataZakupu || row.DataWystawienia || "Brak daty",
        value: calculateTransactionValue(row),
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    setRows(sorted);
  }, [zakupyData, selectedYear, selectedMonth]);

  return (
    <TableWrapper>
      <TitleWrapper>
        🏆 Top 10 Największe Transakcje Zakupowe
        <InfoIconWrapper data-tooltip-id="top10-purchases-tooltip">
          <Info />
        </InfoIconWrapper>
      </TitleWrapper>

      <InfoTooltip
        id="top10-purchases-tooltip"
        place="right"
        style={{
          fontSize: "12px",
          fontFamily: "Arial, sans-serif",
          color: "#fff",
        }}
        content={
          <>
            <strong>Top 10 transakcji zakupowych</strong>
            <br />
            Filtruj po roku i miesiącu. Zestawienie pokazuje 10 największych
            pojedynczych transakcji zakupu, wraz z numerem dokumentu{" "}
            <em>(Dowód Zakupu)</em> i datą.
            <br />
            Wartość liczona jako suma K_42 + K_43.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label style={{ marginRight: 6 }}>Rok:</label>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label style={{ marginRight: 6 }}>Miesiąc:</label>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="all">Cały rok</option>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>
                {monthNames[m - 1]}
              </option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      <StyledTable>
        <thead>
          <tr>
            <th>Miejsce</th>
            <th>Dostawca</th>
            <th>Dowód zakupu</th>
            <th>Data zakupu</th>
            <th>Kwota (PLN)</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map((tx, index) => (
              <tr
                key={`${tx.documentNo}-${index}`}
                className={index < 3 ? "highlight-top" : "highlight-neutral"}
              >
                <td>
                  {index + 1 === 1
                    ? "🥇"
                    : index + 1 === 2
                    ? "🥈"
                    : index + 1 === 3
                    ? "🥉"
                    : index + 1}
                </td>
                <td>{tx.supplierName}</td>
                <td>{tx.documentNo}</td>
                <td>{fmtDate(tx.issueDate)}</td>
                <td>
                  {tx.value.toLocaleString("pl-PL", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5">
                Brak dostępnych danych dla wybranych filtrów.
              </td>
            </tr>
          )}
        </tbody>
      </StyledTable>
    </TableWrapper>
  );
};

export default Top10PurchaseTransactions;
